/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { httpService } from 'src/core/services/api/httpService';
import { AzureResponse, AzureServiceParams } from 'src/core/services/azure/azureContainerService';
import { AzureStorageAccountSwagger } from 'src/core/services/azure/azureSwaggers';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService, ServiceContext } from 'src/core/utils/createService';

export const getAzureStorageAccountService = createService(({ getEnvironment }: ServiceContext<object>) => {
    const { azureBaseUrl } = getEnvironment();

    const buildUrlOptions = (token: string) =>
        luisRequestOptionsBuilder()
            .useContentType('json')
            .useArmToken(token)
            .skipThrottling()
            .build();

    const allowedStorageVersions = ['Storage', 'StorageV2', 'BlobStorage'];

    /**
     * Gets a list of storage accounts for the given tenant and subscription id.
     * @docLink https://docs.microsoft.com/en-us/rest/api/storagerp/storage-accounts/list
     *
     * @param param0.id The subscription id required to fetch all
     * storage accounts groups under it.
     * @param param0.token The tenant token required to fetch
     * storage accounts under it.
     * @param param0.region An optional region to fetch
     * storage accounts under it.
     * @returns A promise with a list of Azure storage accounts.
     */
    const getAzureStorageAccountsBySubscriptionId = async ({
        id,
        token,
        region
    }: Pick<AzureServiceParams, 'token' | 'id'> & { region?: string }): Promise<AzureStorageAccountSwagger[]> => {
        const urlOptions = buildUrlOptions(token);
        const url = urlBuilder()
            .baseUrl(azureBaseUrl)
            .resource('subscriptions', id)
            .resource('providers', 'Microsoft.Storage')
            .resource('storageAccounts')
            .queryParam('api-version', '2021-04-01')
            .build();

        const { data: resources } = await httpService.get<AzureResponse<AzureStorageAccountSwagger[]>>(url, urlOptions);

        const results = resources.value.filter(resource => allowedStorageVersions.some(v => v === resource.kind));

        if (region) {
            return results.filter(r => r.location === region);
        }

        return results;
    };

    return {
        getAzureStorageAccountsBySubscriptionId
    };
});
